import { useCallback } from "react";
import { BarChart3, Scan, Bell, Users, Clock, Sparkles } from "lucide-react";

const benefits = [
  {
    icon: BarChart3,
    title: "Rentabilité temps réel",
    titleJsx: (
      <>
        Rentabilité <span className="text-[#D4AF37]">temps réel</span>
      </>
    ),
    description: "Marge calculée automatiquement à chaque dépense. Vous savez où vous en êtes à tout moment.",
    gradient: "from-[#10B981] to-[#059669]",
    meshColor: "rgba(16,185,129,0.12)",
    meshPos: "20% 30%",
    badge: "Feature #1 la plus utilisée",
    badgeColor: "#10B981",
  },
  {
    icon: Scan,
    title: "Import Scan IA intelligent",
    description: "Importez factures, devis et tickets. Le Scan IA extrait tout automatiquement.",
    gradient: "from-[#F59E0B] to-[#D97706]",
    meshColor: "rgba(245,158,11,0.12)",
    meshPos: "80% 20%",
  },
  {
    icon: Bell,
    title: "Alertes prédictives",
    description: "Soyez prévenu 7, 3 et 1 jour avant qu'un chantier devienne déficitaire.",
    gradient: "from-[#EF4444] to-[#DC2626]",
    meshColor: "rgba(239,68,68,0.10)",
    meshPos: "30% 80%",
  },
  {
    icon: Users,
    title: "Coûts équipe auto",
    description: "Coût journalier calculé par membre. Impact réel visible sur chaque chantier.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
    meshColor: "rgba(139,92,246,0.12)",
    meshPos: "70% 70%",
  },
  {
    icon: Clock,
    title: "Simulation retards",
    description: "Anticipez l'impact des retards sur votre marge avant qu'il ne soit trop tard.",
    gradient: "from-[#06B6D4] to-[#0891B2]",
    meshColor: "rgba(6,182,212,0.12)",
    meshPos: "50% 20%",
  },
  {
    icon: Sparkles,
    title: "Analyse IA",
    description: "L'IA détecte les risques et recommande des actions pour optimiser votre rentabilité.",
    gradient: "from-[#EC4899] to-[#DB2777]",
    meshColor: "rgba(236,72,153,0.14)",
    meshPos: "40% 60%",
    badge: "Propulsé par l'IA",
    badgeColor: "#A855F7",
  },
];

const LandingSolution = () => {
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      card.style.setProperty("--my", `${e.clientY - rect.top}px`);
    },
    []
  );

  return (
    <section id="features" className="relative py-24 lg:py-32 bg-[#08080C]">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-[#F59E0B]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#1A365D]/20 rounded-full blur-[120px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-wider text-white/40 font-medium mb-4 animate-fade-up">
            La solution
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 animate-fade-up [animation-delay:100ms]">
            Pensée pour les
            <span className="block bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent">
              professionnels du BTP
            </span>
          </h2>
          <p className="text-lg text-white/40 animate-fade-up [animation-delay:200ms]">
            Contrôle total sur vos coûts chantiers et affectations d'équipe.
          </p>
        </div>

        {/* Spotlight Grid 3×2 */}
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 group/grid">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className={`group relative rounded-2xl p-7 bg-[#0F0F14] overflow-hidden cursor-default animate-fade-up
                    transition-all duration-350 ease-out
                    group-hover/grid:opacity-60 hover:!opacity-100 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.5)]
                    border border-white/[0.06]
                  `}
                  style={{
                    animationDelay: `${200 + index * 75}ms`,
                    // @ts-expect-error CSS custom properties
                    "--mx": "50%",
                    "--my": "50%",
                  }}
                  onMouseMove={handleMouseMove}
                >
                  {/* Gradient mesh background */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-35 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at ${benefit.meshPos}, ${benefit.meshColor}, transparent 60%)`,
                    }}
                  />

                  {/* Spotlight on hover */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: "radial-gradient(320px circle at var(--mx) var(--my), rgba(255,255,255,0.06), transparent 60%)",
                    }}
                  />

                  {/* Icon + Badge sur la même ligne */}
                  <div className="relative z-10 flex items-center gap-3 mb-5">
                    <div className="h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                      <Icon className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
                    </div>
                    {benefit.badge && (
                      <div
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${benefit.badgeColor}15`,
                          borderColor: `${benefit.badgeColor}30`,
                          borderWidth: 1,
                          color: benefit.badgeColor,
                        }}
                      >
                        <div
                          className="h-1.5 w-1.5 rounded-full animate-pulse"
                          style={{ backgroundColor: benefit.badgeColor }}
                        />
                        {benefit.badge}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="relative z-10 text-base font-semibold text-white mb-2">
                    {"titleJsx" in benefit && benefit.titleJsx
                      ? benefit.titleJsx
                      : benefit.title}
                  </h3>
                  <p className="relative z-10 text-sm text-white/40 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom badge */}
        <div className="mt-16 text-center animate-fade-up [animation-delay:700ms]">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.06]">
            <div className="h-2 w-2 rounded-full bg-[#10B981] animate-pulse" />
            <span className="text-sm text-white/50">
              Conçu pour{" "}
              <span className="text-white/70 font-medium">artisans et PME du BTP</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingSolution;
